export interface NewsFeedMessage {
  id: string | number;
  message: string;
}

export interface NewsConfig {
  blobUrl: string;
  portalUrl: string;
  appId: string;
}

// Pick up a message published while a tab is open without a reload. Matches
// the blob's Cache-Control max-age, so a poll never re-reads a cached copy
// older than ~2 minutes.
export const NEWS_POLL_INTERVAL = 60000;

let newsConfigPromise: Promise<NewsConfig | null> | undefined;

// Per-environment news settings (blob url etc.) live in the managed
// function's app settings - one place to edit - and are served by
// GET /api/news/config (see api/news/index.js). The function is deployed
// with this site, so if this page loaded, the endpoint is reachable; no
// AIMS API involved. A successful response is cached for the session - the
// values only change when an environment is reconfigured - but a failure is
// not, so a transient error at load doesn't disable the feed until the tab
// is reloaded: the next poll retries.
export function fetchNewsConfig(): Promise<NewsConfig | null> {
  if (!newsConfigPromise) {
    newsConfigPromise = fetch("/api/news/config")
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null)
      .then((config: NewsConfig | null) => {
        if (!config) {
          newsConfigPromise = undefined;
        }
        return config;
      });
  }
  return newsConfigPromise;
}

// Reads the news feed from Azure blob storage - the source of truth for the
// feed. Nothing in this app ever writes to the blob; it is published through
// the Admin form or static/news-admin.html. Deliberately no axios, no auth,
// no call to the AIMS API, so the feed keeps working when every other AIMS
// service is down.
export async function fetchNewsFeed(): Promise<NewsFeedMessage | null> {
  const newsConfig = await fetchNewsConfig();
  if (!newsConfig?.blobUrl) {
    return null;
  }

  try {
    const response = await fetch(newsConfig.blobUrl, { cache: "no-cache" });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data.message ? data : null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("Failed to fetch news feed:", error);
    return null;
  }
}
